import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import AddIcon from '@mui/icons-material/Add'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PeopleIcon from '@mui/icons-material/People'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { makeGetRequest, makePostRequest } from 'utils/api'

const CalendarEvents = ({ contactId }) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [createEventOpen, setCreateEventOpen] = useState(false)
  const [eventForm, setEventForm] = useState({
    titre: '',
    designation: '',
    lieux: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    is_all_day: false,
  })

  useEffect(() => {
    fetchEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await makeGetRequest(
        `/api/Social/contact/${contactId}/events/`,
        {},
        true
      )
      if (response.status === 200) {
        setData(response.data)
      } else {
        setError('Failed to load events')
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateEvent = async () => {
    try {
      const response = await makePostRequest(
        `/api/Social/contact/${contactId}/create-event/`,
        eventForm,
        {},
        true
      )
      if (response.status === 200) {
        alert('Event created successfully!')
        setCreateEventOpen(false)
        setEventForm({
          titre: '',
          designation: '',
          lieux: '',
          start_date: '',
          end_date: '',
          start_time: '',
          end_time: '',
          is_all_day: false,
        })
        fetchEvents()
      }
    } catch (err) {
      alert('Failed to create event: ' + err.message)
    }
  }

  const handleFormChange = (field, value) => {
    setEventForm((prev) => ({ ...prev, [field]: value }))
  }

  const renderEvent = (event, isPast = false) => {
    const startDate = event.start_date ? new Date(event.start_date).toLocaleDateString() : 'N/A'
    const endDate = event.end_date ? new Date(event.end_date).toLocaleDateString() : null

    return (
      <Card
        key={event.id}
        sx={{
          mb: 2,
          opacity: isPast ? 0.7 : 1,
          bgcolor: isPast ? 'grey.100' : 'white',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {event.titre}
              </Typography>
              {event.designation && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {event.designation}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EventIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {startDate}
                    {endDate && endDate !== startDate && ` - ${endDate}`}
                  </Typography>
                </Box>

                {!event.is_all_day && (event.start_time || event.end_time) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {event.start_time}
                      {event.end_time && ` - ${event.end_time}`}
                    </Typography>
                  </Box>
                )}

                {event.lieux && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2">{event.lieux}</Typography>
                  </Box>
                )}

                {event.participants_count > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PeopleIcon fontSize="small" color="action" />
                    <Typography variant="body2">{event.participants_count} participant(s)</Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={event.event_type_display}
                  size="small"
                  color={event.event_type === 'a' ? 'primary' : 'default'}
                />
                {event.category && <Chip label={event.category} size="small" variant="outlined" />}
                {event.is_all_day && <Chip label="Toute la journée" size="small" color="info" />}
                {event.is_periodic && <Chip label="Recurrent" size="small" color="secondary" />}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Aucune donnée de calendrier disponible
      </Alert>
    )
  }

  const { summary, upcoming_events, past_events, contact } = data

  return (
    <Box sx={{ p: 2 }}>
      {/* Summary Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: 'white' }}>
            Événements avec {contact.full_name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.400' }}>
            {summary.total_events} total événements ({summary.upcoming_count} événements à venir)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateEventOpen(true)}
        >
          Créer un Événement
        </Button>
      </Box>

      {/* Upcoming Events */}
      <Typography variant="h6" gutterBottom sx={{ color: 'white', mt: 3 }}>
        Événements à venir
      </Typography>
      {upcoming_events.length === 0 ? (
        <Alert severity="info">Aucun événement à venir</Alert>
      ) : (
        upcoming_events.map((event) => renderEvent(event, false))
      )}

      {/* Past Events */}
      {past_events.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ color: 'white', mt: 4 }}>
            Événements passés
          </Typography>
          {past_events.map((event) => renderEvent(event, true))}
        </>
      )}

      {/* Create Event Dialog */}
      <Dialog
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Créer un Événement avec {contact.prenom}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Titre *"
            type="text"
            fullWidth
            variant="outlined"
            value={eventForm.titre}
            onChange={(e) => handleFormChange('titre', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={eventForm.designation}
            onChange={(e) => handleFormChange('designation', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Lieu"
            type="text"
            fullWidth
            variant="outlined"
            value={eventForm.lieux}
            onChange={(e) => handleFormChange('lieux', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Date de début *"
            type="date"
            fullWidth
            variant="outlined"
            value={eventForm.start_date}
            onChange={(e) => handleFormChange('start_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Date de fin"
            type="date"
            fullWidth
            variant="outlined"
            value={eventForm.end_date}
            onChange={(e) => handleFormChange('end_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={eventForm.is_all_day}
                onChange={(e) => handleFormChange('is_all_day', e.target.checked)}
              />
            }
            label="All Day Event"
            sx={{ mb: 2 }}
          />
          {!eventForm.is_all_day && (
            <>
              <TextField
                margin="dense"
                label="Heure de début"
                type="time"
                fullWidth
                variant="outlined"
                value={eventForm.start_time}
                onChange={(e) => handleFormChange('start_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Heure de fin"
                type="time"
                fullWidth
                variant="outlined"
                value={eventForm.end_time}
                onChange={(e) => handleFormChange('end_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEventOpen(false)}>Annuler</Button>
          <Button
            onClick={handleCreateEvent}
            variant="contained"
            disabled={!eventForm.titre || !eventForm.start_date}
          >
            Créer l'Événement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CalendarEvents
